import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { authApi } from '../services/api';
import { useAuthStore } from '../store/auth.store';
import { useBreakpoint } from '../hooks/useBreakpoint';
import { Loader2, ShieldCheck, Smartphone, Copy, Check, AlertTriangle, LogOut } from 'lucide-react';

// A user who lands here with mustSetup2FA cannot use the app at all; if they don't complete
// the setup within this window they are logged out (matches the server-side secret TTL).
const MANDATORY_SETUP_TIMEOUT_MS = 10 * 60 * 1000;

export default function Setup2FAPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, setUser, logout } = useAuthStore();
  const { isDesktop } = useBreakpoint();
  const isMandatory = user?.role !== 'SUPER_ADMIN' && !!user?.mustSetup2FA;

  const handleLogout = async () => {
    try {
      await authApi.logout();
    } catch {
      // Ignore errors
    }
    logout();
    navigate('/login', { replace: true });
  };

  useEffect(() => {
    if (!isMandatory) return;
    const timer = setTimeout(handleLogout, MANDATORY_SETUP_TIMEOUT_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMandatory]);

  // Method selection
  const [duoAvailable, setDuoAvailable] = useState(false);
  const [selectedMethod, setSelectedMethod] = useState<'TOTP' | 'DUO' | null>(null);

  // TOTP states
  const [totpSecret, setTotpSecret] = useState('');
  const [totpQrCode, setTotpQrCode] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [secretCopied, setSecretCopied] = useState(false);
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [showBackupCodes, setShowBackupCodes] = useState(false);

  useEffect(() => {
    authApi.get2FAMethods().then((res) => {
      setDuoAvailable(res.data.duoAvailable);
    }).catch(() => {});
  }, []);

  const setup2FAMutation = useMutation({
    mutationFn: () => authApi.setup2FA(),
    onSuccess: (response) => {
      setTotpSecret(response.data.secret);
      setTotpQrCode(response.data.qrCode);
    },
  });

  const enable2FAMutation = useMutation({
    mutationFn: (data: { secret: string; code: string }) =>
      authApi.enable2FA(data.secret, data.code),
    onSuccess: (response) => {
      setBackupCodes(response.data.backupCodes || []);
      setShowBackupCodes(true);
      if (user) {
        setUser({ ...user, twoFactorEnabled: true, twoFactorMethod: 'TOTP', mustSetup2FA: false });
      }
    },
  });

  const enableDuoMutation = useMutation({
    mutationFn: () => authApi.enableDuo(),
    onSuccess: () => {
      if (user) {
        setUser({ ...user, twoFactorEnabled: true, twoFactorMethod: 'DUO', mustSetup2FA: false });
      }
      navigate(isDesktop ? '/dashboard' : '/map', { replace: true });
    },
  });

  const handleSelectTotp = () => {
    setSelectedMethod('TOTP');
    setup2FAMutation.mutate();
  };

  const handleSelectDuo = () => {
    setSelectedMethod('DUO');
    enableDuoMutation.mutate();
  };

  const handleEnableTotp = () => {
    if (verificationCode.length !== 6) return;
    enable2FAMutation.mutate({ secret: totpSecret, code: verificationCode });
  };

  const handleCopySecret = () => {
    navigator.clipboard.writeText(totpSecret);
    setSecretCopied(true);
    setTimeout(() => setSecretCopied(false), 2000);
  };

  const handleFinish = () => {
    navigate(isDesktop ? '/dashboard' : '/map', { replace: true });
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="max-w-lg mx-auto flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl overflow-hidden">
            <img src="/logo.png" alt="TrackFleet" className="w-full h-full object-cover" />
          </div>
          <div className="flex-1">
            <h1 className="text-lg font-bold text-gray-900">TrackFleet</h1>
            <p className="text-xs text-gray-500">{t('setup_2fa.header_subtitle')}</p>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 px-3 py-2 text-sm text-gray-500 hover:text-gray-700 transition-colors"
          >
            <LogOut className="h-4 w-4" />
            {t('common.logout')}
          </button>
        </div>
      </div>

      {/* Mandatory banner */}
      {isMandatory && !showBackupCodes && (
        <div className="bg-amber-50 border-b border-amber-200 px-6 py-3">
          <div className="max-w-lg mx-auto flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-amber-800">{t('setup_2fa.mandatory_title')}</p>
              <p className="text-xs text-amber-700 mt-0.5">{t('setup_2fa.mandatory_body')}</p>
            </div>
          </div>
        </div>
      )}

      {/* Content */}
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-lg">
          {showBackupCodes ? (
            /* Backup codes screen */
            <div className="text-center">
              <div className="mx-auto w-16 h-16 rounded-2xl bg-amber-50 flex items-center justify-center mb-4">
                <AlertTriangle className="h-8 w-8 text-amber-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('setup_2fa.backup_title')}</h2>
              <p className="text-gray-500 text-sm mb-6">
                {t('setup_2fa.backup_body')}
              </p>

              <div className="bg-gray-900 rounded-xl p-6 mb-6">
                <div className="grid grid-cols-2 gap-2">
                  {backupCodes.map((code, i) => (
                    <code key={i} className="text-emerald-400 font-mono text-sm">{code}</code>
                  ))}
                </div>
              </div>

              <button
                onClick={handleFinish}
                className="w-full px-6 py-3 bg-emerald-600 text-white font-medium rounded-xl hover:bg-emerald-700 transition-colors"
              >
                {t('setup_2fa.backup_continue')}
              </button>
            </div>
          ) : !selectedMethod ? (
            /* Method selection */
            <div className="text-center">
              <div className="mx-auto w-16 h-16 rounded-2xl bg-emerald-50 flex items-center justify-center mb-4">
                <ShieldCheck className="h-8 w-8 text-emerald-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('setup_2fa.select_title')}</h2>
              <p className="text-gray-500 text-sm mb-8">
                {t('setup_2fa.select_body')}
              </p>

              <div className="space-y-4">
                <button
                  onClick={handleSelectTotp}
                  disabled={setup2FAMutation.isPending}
                  className="w-full p-4 bg-white border-2 border-gray-200 rounded-xl hover:border-emerald-500 transition-colors text-left flex items-start gap-4"
                >
                  <div className="w-12 h-12 rounded-xl bg-emerald-50 flex items-center justify-center flex-shrink-0">
                    <Smartphone className="h-6 w-6 text-emerald-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">{t('setup_2fa.method_totp_title')}</h3>
                    <p className="text-sm text-gray-500 mt-1">
                      {t('setup_2fa.method_totp_body')}
                    </p>
                  </div>
                </button>

                {duoAvailable && (
                  <button
                    onClick={handleSelectDuo}
                    disabled={enableDuoMutation.isPending}
                    className="w-full p-4 bg-white border-2 border-gray-200 rounded-xl hover:border-emerald-500 transition-colors text-left flex items-start gap-4"
                  >
                    <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center flex-shrink-0">
                      <ShieldCheck className="h-6 w-6 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">{t('setup_2fa.method_duo_title')}</h3>
                      <p className="text-sm text-gray-500 mt-1">
                        {t('setup_2fa.method_duo_body')}
                      </p>
                      {enableDuoMutation.isPending && (
                        <div className="flex items-center gap-2 mt-2 text-blue-600 text-sm">
                          <Loader2 className="h-4 w-4 animate-spin" />
                          {t('setup_2fa.duo_activating')}
                        </div>
                      )}
                    </div>
                  </button>
                )}
              </div>

              {enableDuoMutation.isError && (
                <div className="mt-4 p-4 rounded-xl bg-red-50 border border-red-200 text-red-600 text-sm">
                  {t('setup_2fa.duo_error')}
                </div>
              )}
            </div>
          ) : selectedMethod === 'TOTP' && totpQrCode ? (
            /* TOTP setup */
            <div className="text-center">
              <div className="mx-auto w-16 h-16 rounded-2xl bg-emerald-50 flex items-center justify-center mb-4">
                <Smartphone className="h-8 w-8 text-emerald-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('setup_2fa.totp_title')}</h2>
              <p className="text-gray-500 text-sm mb-6">
                {t('setup_2fa.totp_body')}
              </p>

              {/* QR Code */}
              <div className="bg-white rounded-xl border border-gray-200 p-6 mb-4 inline-block">
                <img src={totpQrCode} alt={t('setup_2fa.qr_alt')} className="w-48 h-48 mx-auto" />
              </div>

              {/* Secret key */}
              <div className="mb-6">
                <p className="text-xs text-gray-500 mb-2">
                  {t('setup_2fa.manual_key_label')}
                </p>
                <div className="flex items-center justify-center gap-2">
                  <code className="bg-gray-100 px-3 py-1.5 rounded-lg text-sm font-mono text-gray-800 select-all">
                    {totpSecret}
                  </code>
                  <button
                    onClick={handleCopySecret}
                    className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    {secretCopied ? (
                      <Check className="h-4 w-4 text-emerald-500" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </div>

              {/* Verification code */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('setup_2fa.verification_code_label')}
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  className="w-40 mx-auto text-center text-2xl font-mono tracking-widest bg-white border-2 border-gray-300 rounded-xl px-4 py-3 text-gray-900 focus:border-emerald-500 focus:outline-none transition-colors"
                />
              </div>

              {enable2FAMutation.isError && (
                <div className="mb-4 p-4 rounded-xl bg-red-50 border border-red-200 text-red-600 text-sm">
                  {t('setup_2fa.code_invalid_error')}
                </div>
              )}

              <button
                onClick={handleEnableTotp}
                disabled={verificationCode.length !== 6 || enable2FAMutation.isPending}
                className="w-full px-6 py-3 bg-emerald-600 text-white font-medium rounded-xl hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {enable2FAMutation.isPending ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  t('setup_2fa.enable_button')
                )}
              </button>

              <button
                onClick={() => {
                  setSelectedMethod(null);
                  setTotpSecret('');
                  setTotpQrCode('');
                  setVerificationCode('');
                }}
                className="mt-4 text-sm text-gray-500 hover:text-gray-700 transition-colors"
              >
                {t('setup_2fa.choose_other')}
              </button>
            </div>
          ) : (
            /* Loading state */
            <div className="text-center">
              <Loader2 className="h-8 w-8 animate-spin text-emerald-600 mx-auto mb-4" />
              <p className="text-gray-500">{t('setup_2fa.loading')}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
